import { messaging } from '../firebase/config';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// VAPID Key - Firebase Console → Project Settings → Cloud Messaging → Web configuration
const VAPID_KEY = 'BDAdWzkQ034znCm6hHo-X0_4WI5AG_7vitt-SE9omNsiiMpdzZ_CCgYngVD3pTV_jeFZAxIf8wuVoQ2W9_09l4Q';

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  try {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    // Check current permission
    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.log('Notification permission denied');
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get FCM token for current user
 */
export const getFCMToken = async (userId) => {
  try {
    if (!messaging) {
      console.error('Messaging not initialized');
      return null;
    }

    // Check if notifications are supported
    const permission = await requestNotificationPermission();
    if (!permission) {
      console.log('Notification permission not granted');
      return null;
    }

    // Get FCM token
    const token = await getToken(messaging, { 
      vapidKey: VAPID_KEY !== 'YOUR_VAPID_KEY_HERE' ? VAPID_KEY : undefined 
    });
    
    if (token && userId) {
      // Save token to Firestore
      await saveTokenToFirestore(token, userId);
      // Token obtained and saved successfully
    } else {
      console.log('No FCM token available');
    }
    
    return token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    // Common error: messaging/permission-blocked
    if (error.code === 'messaging/permission-blocked') {
      console.log('Notification permission is blocked');
    }
    return null;
  }
};

/**
 * Save FCM token to Firestore
 */
const saveTokenToFirestore = async (token, userId) => {
  try {
    await setDoc(
      doc(db, 'fcmTokens', userId),
      {
        token: token,
        platform: 'web',
        updatedAt: serverTimestamp(),
        enabled: true
      },
      { merge: true }
    );
    console.log('FCM token saved to Firestore');
  } catch (error) {
    console.error('Error saving FCM token to Firestore:', error);
  }
};

/**
 * Delete FCM token (when user disables notifications)
 */
export const deleteFCMToken = async (userId) => {
  try {
    if (!messaging) {
      return;
    }

    // Delete token from Firebase
    try {
      await deleteToken(messaging);
      console.log('FCM token deleted');
    } catch (error) {
      console.error('Error deleting FCM token:', error);
    }

    // Mark as deleted in Firestore
    if (userId) {
      await setDoc(
        doc(db, 'fcmTokens', userId),
        {
          token: null,
          deleted: true,
          enabled: false,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    }
  } catch (error) {
    console.error('Error deleting FCM token:', error);
  }
};

/**
 * Listen for foreground messages (when app is open)
 */
export const onMessageListener = () => {
  return new Promise((resolve) => {
    if (!messaging) {
      resolve(null);
      return;
    }

    onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      resolve(payload);
    });
  });
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'not-supported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
};

